"use client"

import {
  Avatar,
  Box,
  Button,
  Collapsible,
  HStack,
  IconButton,
  Menu,
  Text,
  VStack,
} from "@chakra-ui/react"
import { formatDistanceToNow } from "date-fns/formatDistanceToNow"
import { useRouter } from "next/navigation"
import { useEffect, useReducer, useState } from "react"

import LikeButton from "@/components/LikeButton/LikeButton"
import { Toaster, toaster } from "@/components/ui"
import { useAuth } from "@/contexts/AuthContext"
import { Comment } from "@/types/database"

import CommentForm from "./CommentForm"
import {
  EditIcon,
  EllipsisVIcon,
  MessageSquareReplyIcon,
  TrashIcon,
} from "@/components/icons"

interface CommentState {
  showReplyForm: boolean
  isEditing: boolean
  isDeleting: boolean
  isLiking: boolean
  likesCount: number
}

type CommentAction =
  | { type: "TOGGLE_REPLY" }
  | { type: "HIDE_REPLY" }
  | { type: "START_EDIT" }
  | { type: "STOP_EDIT" }
  | { type: "DELETE_START" }
  | { type: "DELETE_END" }
  | { type: "LIKE_START" }
  | { type: "LIKE_COMPLETE"; likes: number }
  | { type: "LIKE_FAILED" }

function commentReducer(
  state: CommentState,
  action: CommentAction
): CommentState {
  switch (action.type) {
    case "TOGGLE_REPLY":
      return { ...state, showReplyForm: !state.showReplyForm }
    case "HIDE_REPLY":
      return { ...state, showReplyForm: false }
    case "START_EDIT":
      return { ...state, isEditing: true }
    case "STOP_EDIT":
      return { ...state, isEditing: false }
    case "DELETE_START":
      return { ...state, isDeleting: true }
    case "DELETE_END":
      return { ...state, isDeleting: false }
    case "LIKE_START":
      return { ...state, isLiking: true }
    case "LIKE_COMPLETE":
      return { ...state, isLiking: false, likesCount: action.likes }
    case "LIKE_FAILED":
      return { ...state, isLiking: false }
  }
}

interface CommentItemProps {
  comment: Comment
  postId: string
  onCommentSubmitted?: () => void
  depth?: number
  likedComments: Set<string>
  onLikeUpdate: (commentId: string, isLiked: boolean) => void
}

const CommentItem = ({
  comment,
  depth = 0,
  likedComments,
  onCommentSubmitted,
  onLikeUpdate,
  postId,
}: CommentItemProps) => {
  const { session } = useAuth()
  const [
    { isDeleting, isEditing, isLiking, likesCount, showReplyForm },
    dispatch,
  ] = useReducer(commentReducer, {
    isDeleting: false,
    isEditing: false,
    isLiking: false,
    likesCount: Array.isArray(comment.likes) ? comment.likes.length : 0,
    showReplyForm: false,
  })
  const [relativeCreatedAt, setRelativeCreatedAt] = useState<string | null>(
    null
  )
  const hasLiked = likedComments.has(comment.id)
  const maxDepth = 3 // Limit nesting depth
  const canReply = depth < maxDepth

  const { push } = useRouter()
  // Check if current user can edit/delete this comment
  const canModify = session?.user?.email === comment.author_email

  useEffect(() => {
    setRelativeCreatedAt(
      formatDistanceToNow(new Date(comment.date_created), {
        addSuffix: true,
      })
    )
  }, [comment.date_created])

  const handleReplySubmitted = () => {
    dispatch({ type: "HIDE_REPLY" })
    onCommentSubmitted?.()
  }

  const handleEditSubmitted = () => {
    dispatch({ type: "STOP_EDIT" })
    onCommentSubmitted?.()
  }

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return
    }

    dispatch({ type: "DELETE_START" })
    try {
      const response = await fetch(`/api/comments/${comment.id}`, {
        method: "DELETE",
      })

      if (response.ok) {
        toaster.create({
          duration: 3000,
          title: "Comment deleted successfully",
          type: "success",
        })
        onCommentSubmitted?.()
      } else {
        throw new Error("Failed to delete comment")
      }
    } catch (error) {
      toaster.create({
        description: "Please try again later",
        duration: 3000,
        title: "Failed to delete comment",
        type: "error",
      })
    } finally {
      dispatch({ type: "DELETE_END" })
    }
  }

  const handleLike = async () => {
    if (!session) {
      toaster.create({
        action: {
          label: "Login",
          onClick: () => push("#comments"),
        },
        closable: true,
        description: "You must sign in to like or add responses, in the form",
        title: "Sign in required",
        type: "info",
      })
      return
    }

    dispatch({ type: "LIKE_START" })
    try {
      const response = await fetch(`/api/comments/${comment.id}/like`, {
        method: "POST",
      })

      if (response.ok) {
        const result = await response.json()
        dispatch({ likes: result.likes, type: "LIKE_COMPLETE" })
        onLikeUpdate(comment.id, result.has_liked)
      } else {
        throw new Error("Failed to like comment")
      }
    } catch (error) {
      toaster.create({
        description: "Please try again later",
        duration: 3000,
        title: "Failed to like comment",
        type: "error",
      })
      dispatch({ type: "LIKE_FAILED" })
    }
  }

  return (
    <>
      <HStack alignItems={"flex-start"} className="responseItem" flexGrow={1}>
        <Toaster />
        <Avatar.Root ml={depth > 0 ? 4 : 0}>
          <Avatar.Fallback name={comment.author_name} />
          <Avatar.Image src={comment.avatar_url} />
        </Avatar.Root>
        <VStack
          _dark={{ bg: depth > 0 ? "#101925" : "#151f2c" }}
          alignItems={"stretch"}
          bg={depth > 0 ? "bg.emphasized" : "bg.subtle"}
          borderRadius="md"
          borderWidth={1}
          className="response"
          flexGrow={1}
          p={4}
        >
          <HStack gap={3} justify="space-between" mb={3}>
            <HStack gap={3}>
              <VStack align="start" gap={0}>
                <Text fontSize="sm" fontWeight="semibold">
                  {comment.author_name}
                </Text>
                <Text color="fg.muted" fontSize="xs">
                  {relativeCreatedAt ?? comment.date_created}
                </Text>
              </VStack>
            </HStack>

            <HStack>
              {!isEditing && (
                <LikeButton
                  disabled={isLiking}
                  handleLike={handleLike}
                  isLiked={hasLiked}
                  likeCount={likesCount.toString()}
                  loading={isLiking}
                />
              )}
              {canReply && (
                <Button
                  _hover={{ color: "primary.500" }}
                  colorPalette="gray"
                  onClick={() => dispatch({ type: "TOGGLE_REPLY" })}
                  rounded={"xl"}
                  size="lg"
                  variant="ghost"
                >
                  <MessageSquareReplyIcon />
                  {showReplyForm ? "Cancel" : "Reply"}
                  {comment.replies && comment.replies.length > 0 && (
                    <Text as="span" color="gray.500" fontSize="xs" ml={1}>
                      ({comment.replies.length})
                    </Text>
                  )}
                </Button>
              )}

              {canModify && (
                <Menu.Root>
                  <Menu.Trigger aria-label="Comment options" asChild>
                    <IconButton
                      aria-label="Comment options"
                      size="sm"
                      variant="ghost"
                    >
                      <EllipsisVIcon />
                    </IconButton>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item
                        color={"fg.emphasized"}
                        cursor={"pointer"}
                        onClick={() => dispatch({ type: "START_EDIT" })}
                        value="edit"
                      >
                        <EditIcon />
                        Edit
                      </Menu.Item>
                      <Menu.Item
                        color={"fg.error"}
                        cursor={"pointer"}
                        disabled={isDeleting}
                        onClick={handleDelete}
                        value="delete"
                      >
                        <TrashIcon />
                        {isDeleting ? "Deleting..." : "Delete"}
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              )}
            </HStack>
          </HStack>

          {isEditing ? (
            <Box
              _dark={{ bg: "gray.800" }}
              bg={"white"}
              borderRadius="md"
              borderWidth={1}
              mb={3}
              p={4}
            >
              <CommentForm
                editingComment={comment}
                isReply={false}
                onCancel={() => dispatch({ type: "STOP_EDIT" })}
                onCommentSubmitted={handleEditSubmitted}
                parentId={comment.parent_id as string}
                postId={postId}
              />
            </Box>
          ) : (
            <Text fontSize="sm" lineHeight="1.6" mb={3} whiteSpace="pre-wrap">
              {comment.comment}
            </Text>
          )}

          {!isEditing && canReply && showReplyForm && (
            <Collapsible.Root open={showReplyForm}>
              <Collapsible.Content>
                <Box
                  _dark={{ bg: "gray.800" }}
                  bg={"white"}
                  borderRadius="md"
                  borderWidth={1}
                  mt={4}
                  p={4}
                >
                  <CommentForm
                    isReply={true}
                    onCancel={() => dispatch({ type: "HIDE_REPLY" })}
                    onCommentSubmitted={handleReplySubmitted}
                    parentId={comment.id}
                    postId={postId}
                  />
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          )}
        </VStack>
      </HStack>

      {/* Render replies */}
      {comment.replies && comment.replies.length > 0 && (
        <VStack align="stretch" gap={1}>
          {comment.replies.map((reply) => (
            <CommentItem
              comment={reply}
              depth={depth + 1}
              key={reply.id}
              likedComments={likedComments}
              onCommentSubmitted={onCommentSubmitted}
              onLikeUpdate={onLikeUpdate}
              postId={postId}
            />
          ))}
        </VStack>
      )}
    </>
  )
}

export default CommentItem
